import { CreatorProfileInput, SocialNetwork } from '@/interfaces/creator-profile.interface'
import { SocialNetworksAvailables } from '@/interfaces/socialnetworks.interface'
import User, { Identity } from '@/interfaces/user.interface'
import onUserChange from '@observables/user.observable'
import HubCoreService from '@services/hub-core/hub-core.service'
import LogService from '@services/log/log.service'
import DataLayerHelper from './data-layer.helper'

interface Payload {
  profileId: string
  currentState: boolean
  socialNetwork: SocialNetwork
}

export interface ProfileActivation {
  profileId?: string
  hasCreatorsInsights?: boolean
  isSharedCreatorsInsights?: boolean
  socialNetwork?: SocialNetworksAvailables
  token?: string
}

class ActivateProfile {
  user: User
  hubCoreService = new HubCoreService()
  dataLayerHelper = new DataLayerHelper()
  logService = new LogService()

  constructor() {
    onUserChange?.subscribe((user) => {
      this.user = user
    })
  }

  public async activateProfileForCreatorsInsights({ profileId, currentState, socialNetwork }: Payload) {
    const activateCreatorProfileDto = this.createCreatorsInsightsDto(currentState, socialNetwork)

    try {
      await this.hubCoreService.activateCreatorProfile(profileId, activateCreatorProfileDto)
      await this.sendDataLayerEvent(profileId, 'creators_insights_active', activateCreatorProfileDto.hasCreatorsInsights, socialNetwork)
      this.updateAndNotifyUserIdentities(profileId, { hasCreatorsInsights: !currentState })
    } catch (error: any) {
      this.handleActivationError(error)
    }
  }

  public async activateProfileForPublicView({ profileId, currentState, socialNetwork }: Payload) {
    const activateCreatorProfileDto = this.createPublicViewDto(currentState, socialNetwork)

    try {
      await this.hubCoreService.activateCreatorProfile(profileId, activateCreatorProfileDto)
      await this.sendDataLayerEvent(
        profileId,
        'is_shared_creators_insights',
        activateCreatorProfileDto.isSharedCreatorsInsights,
        socialNetwork
      )
      this.updateAndNotifyUserIdentities(profileId, { isSharedCreatorsInsights: !currentState })
    } catch (error: any) {
      this.handleActivationError(error)
    }
  }

  private createCreatorsInsightsDto(currentState: boolean, socialNetwork: SocialNetwork): CreatorProfileInput {
    const dto: CreatorProfileInput = {
      hasCreatorsInsights: !currentState,
      socialNetwork,
    }
    if (!dto.hasCreatorsInsights) {
      dto.isSharedCreatorsInsights = false
    }
    return dto
  }

  private createPublicViewDto(currentState: boolean, socialNetwork: SocialNetwork) {
    return {
      isSharedCreatorsInsights: !currentState,
      socialNetwork,
    }
  }

  private async sendDataLayerEvent(profileId: string, event: string, state: boolean, socialNetwork: string) {
    const profileName = this.user?.identities?.find((identity) => identity?.id === profileId)?.username ?? ''
    await this.dataLayerHelper?.sendToDataLayer({
      event,
      profile: profileName,
      socialNetwork,
      return: state,
      context: 'out creators-insights',
    })
  }

  private updateAndNotifyUserIdentities(
    profileId: string,
    updates: Partial<Identity & { hasCreatorsInsights: boolean; isSharedCreatorsInsights: boolean }>
  ) {
    const profilesUpdated = this.user?.identities?.map((identity) => (identity.id === profileId ? { ...identity, ...updates } : identity))
    onUserChange?.next({ ...this.user, identities: profilesUpdated })
  }

  private handleActivationError(error: any) {
    this.logService.error({ message: 'Error on toggle creator insights', error })
    throw error
  }
}

export default ActivateProfile
